import { BOX } from './railLayout';
import { DEFECT, DEFECT_DARK } from './railPalette';
import { LOOP_SECONDS, PLATE_LIFT, inspectionWindow } from './railTiming';

/**
 * ### The turn
 *
 * On the plate every unit is turned about its vertical axis, a full
 * revolution one way and then a full revolution back, so the microphone
 * hears it all round. The drawing is flat, so the turn cannot be
 * a rotation of the picture: that would roll the cube like a wheel. Instead
 * the cube's corners are moved round the footprint as a real yaw would move
 * them, and since a face is a parallelogram before and after, each face is
 * carried from its resting shape to its turned shape by one affine map.
 * That map is written as a list of transform functions rather than one
 * `matrix()`: a browser interpolates matching function lists component by
 * component, but interpolates matrices by decomposing them, and a face
 * turning edge-on is a nearly singular matrix whose decomposition swings
 * wildly between samples. Faces are hidden while they face away, and on the
 * defect the faces are shaded by which way they point, so the light stays
 * where it is while the unit turns under it.
 */

/**
 * ### SideFaceId
 *
 * The four vertical faces, named by where they stand when the unit is at
 * rest. `left` and `right` are the two seen at rest; the back pair come
 * round during the turn.
 */
export type SideFaceId = 'left' | 'right' | 'backRight' | 'backLeft';

/**
 * ### SideFace
 *
 * A vertical face of the cube, fixed by the world angle of the footprint
 * corner it starts from. It runs to the corner a quarter turn on. World
 * angles put the right corner at 0°, the front corner at 90°.
 */
export interface SideFace {
  id: SideFaceId;
  /** World angle, in degrees, of the corner the face starts from */
  from: number;
}

/**
 * ### SIDE_FACES
 *
 * The faces in painting order: the back pair first so that whichever of
 * them is showing at any moment sits behind the front pair.
 */
export const SIDE_FACES: ReadonlyArray<SideFace> = [
  { id: 'backLeft', from: 270 },
  { id: 'backRight', from: 0 },
  { id: 'left', from: 180 },
  { id: 'right', from: 90 },
];

/** Degrees of yaw between keyframe samples of the turn. */
const SAMPLE_STEP_DEGREES = 12;

/** Coarser samples for the shading, which changes slowly and blends linearly. */
const SHADE_STEP_DEGREES = 24;

/**
 * How long a face takes to switch sides at edge-on, in seconds: long enough
 * to be two distinct keyframes, short enough to read as a cut.
 */
const EDGE_SWITCH_SECONDS = 0.005;

/** World angle the light comes from: the front-left face is the lit one at rest. */
const LIGHT_ANGLE = 135;

interface Point {
  x: number;
  y: number;
}

const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;

/** Screen position of the footprint corner at world `angle`, before the base translate. */
function corner(angle: number): Point {
  return {
    x: BOX.halfWidth * Math.cos(toRadians(angle)),
    y: -BOX.halfDepth + BOX.halfDepth * Math.sin(toRadians(angle)),
  };
}

/** The face's two bottom corners after `yaw` degrees of turn. */
function faceCorners(face: SideFace, yaw: number): [Point, Point] {
  return [corner(face.from + yaw), corner(face.from - 90 + yaw)];
}

/** World angle the face's outward normal points along after `yaw`. */
function faceNormal(face: SideFace, yaw: number): number {
  return face.from - 45 + yaw;
}

/**
 * ### isSideFaceVisible
 *
 * Whether a face points toward the viewer after `yaw` degrees of turn. The
 * viewer is at the front, world angle 90°, so a face shows while its normal
 * has any forward component.
 *
 * @param face The face to test
 * @param yaw Degrees of turn from rest
 * @returns True while the face is on the near side of the unit
 */
export function isSideFaceVisible(face: SideFace, yaw: number): boolean {
  return Math.sin(toRadians(faceNormal(face, yaw))) > 0;
}

/**
 * ### sideFacePoints
 *
 * The face's outline at rest, as an SVG `points` list: its two bottom
 * corners and the same two lifted by the cube's height.
 *
 * @param face The face to draw
 * @returns Points in the box's own coordinates, origin at the bottom-front corner
 */
export function sideFacePoints(face: SideFace): string {
  const [a, b] = faceCorners(face, 0);

  return [
    `${a.x},${a.y}`,
    `${b.x},${b.y}`,
    `${b.x},${b.y - BOX.height}`,
    `${a.x},${a.y - BOX.height}`,
  ].join(' ');
}

/**
 * ### topFacePoints
 *
 * The lid at rest: the footprint diamond lifted by the cube's height.
 *
 * @returns Points in the box's own coordinates
 */
export function topFacePoints(): string {
  return [90, 0, 270, 180]
    .map((angle) => corner(angle))
    .map(({ x, y }) => `${x},${y - BOX.height}`)
    .join(' ');
}

const fixed = (value: number): string =>
  value.toFixed(3).replace(/\.?0+$/, '') || '0';

const toDegrees = (radians: number): number => (radians * 180) / Math.PI;

/**
 * The map from a side face at rest to the same face after `yaw`. Vertical
 * edges stay vertical and keep their height, so it is a horizontal scale
 * with a vertical shear, anchored on the face's first corner. Written as
 * translate, scale, skewY: applied right to left, the skew tilts the face,
 * the scale sets its width, and the translate moves its anchor corner to
 * where the turn has carried it. The scale passes through zero at edge-on
 * and comes out negative on the far side, which a plain number does
 * without complaint.
 */
function sideFaceTransform(face: SideFace, yaw: number): string {
  const [a0, b0] = faceCorners(face, 0);
  const [a, b] = faceCorners(face, yaw);
  const scaleX = (b.x - a.x) / (b0.x - a0.x);
  const shearY = (b.y - a.y - (b0.y - a0.y)) / (b0.x - a0.x);
  const translateX = a.x - scaleX * a0.x;
  const translateY = a.y - (shearY * a0.x + a0.y);

  return [
    `translate(${fixed(translateX)}px, ${fixed(translateY)}px)`,
    `scale(${fixed(scaleX)}, 1)`,
    `skewY(${fixed(toDegrees(Math.atan(shearY)))}deg)`,
  ].join(' ');
}

/**
 * The lid seen from above is a square turning in place; seen in the drawing
 * it is that turn squashed to the belt's depth ratio, about the lid's
 * centre. Written out as that: move the centre to the origin, undo the
 * squash, rotate, squash again, move back. Only the angle changes between
 * keyframes, so the interpolated lid is exactly the lid at the
 * interpolated angle.
 */
function topFaceTransform(yaw: number): string {
  const ratio = BOX.halfDepth / BOX.halfWidth;
  const centreY = -(BOX.height + BOX.halfDepth);

  return [
    `translate(0px, ${fixed(centreY)}px)`,
    `scale(1, ${fixed(ratio)})`,
    `rotate(${fixed(yaw)}deg)`,
    `scale(1, ${fixed(1 / ratio)})`,
    `translate(0px, ${fixed(-centreY)}px)`,
  ].join(' ');
}

const hexChannel = (hex: string, offset: number): number =>
  parseInt(hex.slice(offset, offset + 2), 16);

function mixHex(from: string, to: string, amount: number): string {
  const channel = (offset: number): string =>
    Math.round(
      hexChannel(from, offset) +
        (hexChannel(to, offset) - hexChannel(from, offset)) * amount,
    )
      .toString(16)
      .padStart(2, '0');

  return `#${channel(1)}${channel(3)}${channel(5)}`;
}

/**
 * ### sideFaceShade
 *
 * The defect's colour for a face pointing where `yaw` has turned it: the
 * full red on the face that squarely meets the light, darkening to the
 * shadow red as it turns away.
 *
 * @param face The face to colour
 * @param yaw Degrees of turn from rest
 * @returns A hex colour
 */
export function sideFaceShade(face: SideFace, yaw: number): string {
  const facing = Math.cos(toRadians(faceNormal(face, yaw) - LIGHT_ANGLE));

  return mixHex(DEFECT_DARK, DEFECT, Math.max(0, Math.min(1, facing)));
}

/**
 * How far one turn has got, as a share of its revolution, at `progress`
 * through it. Eased at both ends so the plate starts and stops rather than
 * snapping.
 */
function easedTurn(progress: number): number {
  return progress - Math.sin(2 * Math.PI * progress) / (2 * Math.PI);
}

/** The share of a turn's time at which it has covered `share` of its revolution. */
function progressOf(share: number): number {
  let low = 0;
  let high = 1;
  for (let step = 0; step < 40; step += 1) {
    const mid = (low + high) / 2;
    if (easedTurn(mid) < share) low = mid;
    else high = mid;
  }

  return (low + high) / 2;
}

/**
 * Both moments, in the box's own time, at which the unit is at `yaw`: once
 * on the way round, when the yaw climbs from 0° to 360° over the first
 * turn, and once on the way back, when it falls again over the second, so
 * the unit comes to rest exactly as it arrived.
 */
function turnTimesAt(yaw: number): [outward: number, back: number] {
  const { up, reverse } = inspectionWindow();
  const fraction = progressOf(yaw / 360);

  return [
    up + fraction * PLATE_LIFT.turn,
    reverse + (1 - fraction) * PLATE_LIFT.turn,
  ];
}

interface TurnSample {
  /** Seconds into the box's own loop */
  at: number;
  /** Degrees of turn from rest at that moment */
  yaw: number;
}

/**
 * The yaws sampled `step` degrees apart across both turns, each with the
 * moment it is reached, plus the three ends: rest as the first turn
 * starts, the full 360° as it reverses, and rest again as the second
 * finishes. The 360° is kept as 360° rather than folded to 0°, so the lid
 * is interpolated on round to it and back from it, not spun the short way.
 */
function turnSamples(step: number): Array<TurnSample> {
  const { up, reverse, down } = inspectionWindow();
  const samples: Array<TurnSample> = [
    { at: up, yaw: 0 },
    { at: reverse, yaw: 360 },
    { at: down, yaw: 0 },
  ];

  for (let yaw = step; yaw < 360; yaw += step) {
    const [outward, back] = turnTimesAt(yaw);
    samples.push({ at: outward, yaw }, { at: back, yaw });
  }

  return samples;
}

interface Stop {
  at: number;
  rules: string;
}

const pct = (seconds: number): string =>
  `${((seconds / LOOP_SECONDS) * 100).toFixed(4)}%`;

function keyframes(name: string, stops: ReadonlyArray<Stop>): string {
  const body = [...stops]
    .sort((left, right) => left.at - right.at)
    .map(({ at, rules }) => `${pct(at)} { ${rules} }`)
    .join(' ');

  return `@keyframes ${name} { ${body} }`;
}

/** The stops that hold a face at its drawn state outside the turns. */
function restingStops(rules: string): Array<Stop> {
  const { start, end } = inspectionWindow();

  return [
    { at: 0, rules },
    { at: start, rules },
    { at: end, rules },
    { at: LOOP_SECONDS, rules },
  ];
}

/** The yaws in (0°, 360°) at which the face is edge-on and changes sides. */
function edgeOnYaws(face: SideFace): Array<number> {
  const normal = faceNormal(face, 0);

  return [(-normal + 720) % 360, (180 - normal + 720) % 360];
}

/**
 * The keyframes that turn one side face: its shape at every sample through
 * both turns, hidden or not, so the shape is always between two near
 * neighbours; and its visibility cutting over at each edge-on, which it
 * passes going round and again coming back, the other way about.
 */
function sideFaceKeyframes(face: SideFace): string {
  const stops = restingStops(
    `transform: ${sideFaceTransform(face, 0)}; opacity: ${isSideFaceVisible(face, 0) ? 1 : 0};`,
  );

  for (const { at, yaw } of turnSamples(SAMPLE_STEP_DEGREES)) {
    stops.push({ at, rules: `transform: ${sideFaceTransform(face, yaw)};` });
  }

  for (const edge of edgeOnYaws(face)) {
    const below = isSideFaceVisible(face, edge - 1) ? 1 : 0;
    const above = isSideFaceVisible(face, edge + 1) ? 1 : 0;
    const [outward, back] = turnTimesAt(edge);
    stops.push(
      { at: outward - EDGE_SWITCH_SECONDS, rules: `opacity: ${below};` },
      { at: outward, rules: `opacity: ${above};` },
      { at: back - EDGE_SWITCH_SECONDS, rules: `opacity: ${above};` },
      { at: back, rules: `opacity: ${below};` },
    );
  }

  return keyframes(`prl-face-${face.id}`, stops);
}

/** The defect's shading for one face across both turns; fill only. */
function sideFaceShadeKeyframes(face: SideFace): string {
  const stops = restingStops(`fill: ${sideFaceShade(face, 0)};`);

  for (const { at, yaw } of turnSamples(SHADE_STEP_DEGREES)) {
    stops.push({ at, rules: `fill: ${sideFaceShade(face, yaw)};` });
  }

  return keyframes(`prl-face-${face.id}-shade`, stops);
}

/** The lid turning in place round and back, resting flat outside the turns. */
function topFaceKeyframes(): string {
  const stops = restingStops(`transform: ${topFaceTransform(0)};`);

  for (const { at, yaw } of turnSamples(SAMPLE_STEP_DEGREES)) {
    stops.push({ at, rules: `transform: ${topFaceTransform(yaw)};` });
  }

  return keyframes('prl-face-top', stops);
}

/**
 * ### turnKeyframes
 *
 * Every keyframe the turns need: one per side face for its shape and
 * visibility, one per side face for the defect's shading, and one for the
 * lid. The keyframes are in the box's own time, like the travel, so the
 * same negative delay staggers both.
 *
 * @returns CSS text to ship inside the drawing's stylesheet
 */
export function turnKeyframes(): string {
  return [
    ...SIDE_FACES.map(sideFaceKeyframes),
    ...SIDE_FACES.map(sideFaceShadeKeyframes),
    topFaceKeyframes(),
  ].join('\n');
}
